import Add from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEvent, useState } from "react";

import { BotOptionSpec } from "features/adminUsers/adminUsers";
import {
  OPTION_KEYS,
  parseOptions,
  readListOption,
  readMapOption,
  readOption,
  writeListOption,
  writeMapOption,
  writeOption,
} from "utils/additionalOptions";

type Props = {
  /** What this bot declares it reads (BotRegistry.metadata.options). */
  options: BotOptionSpec[];
  /** Raw `additional_options`; these fields edit keys inside it. */
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
};

function FieldFrame({
  spec,
  children,
}: {
  spec: BotOptionSpec;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="subtitle2" fontWeight={700}>
        {spec.label}
        {spec.required ? " *" : ""}
      </Typography>
      {spec.help ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 0.5 }}
        >
          {spec.help}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
}

function ListField({
  spec,
  items,
  onChange,
  disabled,
}: {
  spec: BotOptionSpec;
  items: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const rows = items.length ? items : [""];
  return (
    <FieldFrame spec={spec}>
      {rows.map((item, i) => (
        <Stack
          key={i}
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ mb: 0.75 }}
        >
          <TextField
            size="small"
            fullWidth
            value={item}
            disabled={disabled}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChange(rows.map((v, j) => (j === i ? e.target.value : v)))
            }
          />
          <IconButton
            size="small"
            color="error"
            disabled={disabled || (rows.length === 1 && !rows[0])}
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Button
        size="small"
        startIcon={<Add fontSize="small" />}
        disabled={disabled}
        onClick={() => onChange([...rows, ""])}
      >
        Add
      </Button>
    </FieldFrame>
  );
}

function MapField({
  spec,
  rows,
  onChange,
  disabled,
}: {
  spec: BotOptionSpec;
  rows: [string, string][];
  onChange: (next: [string, string][]) => void;
  disabled?: boolean;
}) {
  return (
    <FieldFrame spec={spec}>
      {rows.map(([k, v], i) => (
        <Stack
          key={i}
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ mb: 0.75 }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder="Folder"
            value={k}
            disabled={disabled}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChange(
                rows.map((r, j) => (j === i ? [e.target.value, r[1]] : r)) as [
                  string,
                  string,
                ][],
              )
            }
          />
          <TextField
            size="small"
            fullWidth
            placeholder="Document type"
            value={v}
            disabled={disabled}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChange(
                rows.map((r, j) => (j === i ? [r[0], e.target.value] : r)) as [
                  string,
                  string,
                ][],
              )
            }
          />
          <IconButton
            size="small"
            color="error"
            disabled={
              disabled || (rows.length === 1 && !rows[0][0] && !rows[0][1])
            }
            onClick={() =>
              onChange(rows.filter((_, j) => j !== i) as [string, string][])
            }
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Button
        size="small"
        startIcon={<Add fontSize="small" />}
        disabled={disabled}
        onClick={() => onChange([...rows, ["", ""]] as [string, string][])}
      >
        Add row
      </Button>
    </FieldFrame>
  );
}

/** Renders whatever the selected bot declares it reads. */
export default function BotOptionFields({
  options,
  value,
  onChange,
  disabled,
}: Props) {
  // Lists and map rows are local: a half-typed row cannot be represented in the
  // stored value, and deriving it back would wipe what is being typed.
  const [lists, setLists] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(
      options
        .filter((o) => o.type === "list")
        .map((o) => [o.key, readListOption(value, o.key)]),
    ),
  );
  const [showRaw, setShowRaw] = useState(false);
  const [maps, setMaps] = useState<Record<string, [string, string][]>>(() =>
    Object.fromEntries(
      options
        .filter((o) => o.type === "map")
        .map((o) => {
          const entries = Object.entries(readMapOption(value, o.key));
          return [o.key, entries.length ? entries : [["", ""]]];
        }),
    ),
  );

  // Undeclared keys still have to be reachable: hand-configured jobs carry them.
  const declared = new Set([
    ...options.map((o) => o.key.toUpperCase()),
    OPTION_KEYS.skipErrorEmployees,
  ]);
  const undeclared = parseOptions(value).filter(
    (t) => !declared.has(t.key.toUpperCase()),
  );

  if (!options.length && !undeclared.length) return null;

  return (
    <>
      {options.map((spec) => {
        if (spec.type === "list") {
          return (
            <ListField
              key={spec.key}
              spec={spec}
              items={lists[spec.key] ?? []}
              disabled={disabled}
              onChange={(next) => {
                setLists((prev) => ({ ...prev, [spec.key]: next }));
                onChange(writeListOption(value, spec.key, next));
              }}
            />
          );
        }
        if (spec.type === "map") {
          const rows = maps[spec.key] ?? [["", ""]];
          return (
            <MapField
              key={spec.key}
              spec={spec}
              rows={rows}
              disabled={disabled}
              onChange={(next) => {
                const safe = next.length
                  ? next
                  : ([["", ""]] as [string, string][]);
                setMaps((prev) => ({ ...prev, [spec.key]: safe }));
                onChange(
                  writeMapOption(value, spec.key, Object.fromEntries(next)),
                );
              }}
            />
          );
        }
        if (spec.type === "bool") {
          const raw = (readOption(value, spec.key) || "").toLowerCase();
          const checked = raw ? raw === "true" : Boolean(spec.default);
          return (
            <FormControlLabel
              key={spec.key}
              sx={{ mb: 1, display: "block" }}
              control={
                <Switch
                  size="small"
                  checked={checked}
                  disabled={disabled}
                  onChange={(e) =>
                    onChange(
                      writeOption(
                        value,
                        spec.key,
                        e.target.checked ? "True" : "False",
                      ),
                    )
                  }
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  {spec.label}
                  {spec.help ? ` - ${spec.help}` : ""}
                </Typography>
              }
            />
          );
        }
        return (
          <FieldFrame key={spec.key} spec={spec}>
            <TextField
              size="small"
              fullWidth
              select={Boolean(spec.choices?.length)}
              type={spec.type === "int" ? "number" : "text"}
              value={readOption(value, spec.key) ?? ""}
              placeholder={spec.default != null ? String(spec.default) : ""}
              disabled={disabled}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChange(
                  writeOption(value, spec.key, e.target.value.trim() || null),
                )
              }
            >
              {(spec.choices ?? []).map((choice) => (
                <MenuItem key={choice} value={choice}>
                  {choice}
                </MenuItem>
              ))}
            </TextField>
          </FieldFrame>
        );
      })}

      {undeclared.length ? (
        <Box sx={{ mb: 1.5 }}>
          <Button size="small" onClick={() => setShowRaw((v) => !v)}>
            {showRaw ? "Hide" : "Show"} {undeclared.length} option(s) this bot
            does not declare
          </Button>
          {showRaw ? (
            <TextField
              sx={{ mt: 0.5 }}
              fullWidth
              multiline
              minRows={2}
              label="Raw options"
              value={value}
              disabled={disabled}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChange(e.target.value)
              }
            />
          ) : null}
        </Box>
      ) : null}
    </>
  );
}
